// src/stage2/sceneExterior/RingsField.jsx
import React from 'react';
import Ring from './Ring';

// simple helpers
function lerp(a, b, t) {
  return a + (b - a) * t;
}

function lerp3(p0, p1, t) {
  return [
    lerp(p0[0], p1[0], t),
    lerp(p0[1], p1[1], t),
    lerp(p0[2], p1[2], t),
  ];
}

// Sample N positions along a polyline of points
function samplePath(points, count) {
  const result = [];
  if (!points || points.length < 2 || count <= 0) return result;

  const segments = points.length - 1;

  for (let i = 0; i < count; i++) {
    const u = count === 1 ? 0.5 : i / (count - 1); // 0 → 1
    const f = u * segments;
    const segIndex = Math.min(segments - 1, Math.floor(f));
    const localT = f - segIndex;

    const p0 = points[segIndex];
    const p1 = points[segIndex + 1];

    result.push(lerp3(p0, p1, localT));
  }

  return result;
}

export function RingsField({onRingCollect}) {
  const clustersConfig = [
    {
      id: 'to-bikes-path',
      type: 'path',
      points: [
        // x, y, z 
        [0,    1.9,   6],
        [3,    2.5,   9],
        [6.5,  2.4,  12],
        [9.5,  2.3,  15],
        [12.5, 2.4,  18],
      ],
      count: 6,
      baseScale: 0.25,
      scaleWave: 0.2,
    },
    {
      id: 'to-front-path',
      type: 'path',
      points: [
        // x, y, z 
        [0, 2.1,  7.5],
        [0, 2.5, 11],
        [0, 2.3, 17],
        [0, 2.4, 20],
      ],
      count: 5,
      baseScale: 0.25,
      scaleWave: 0.2,
    },
        {
      id: 'to-back-hill',
      type: 'path',
      points: [
        // x, y, z 
        [-16, 1, -8],
        [-32, 2.5, -9],
      ],
      count: 7,
      baseScale: 0.25,
      scaleWave: 0.2,
    },
    // 🔹 2) You can leave these or delete them if you only want the path for now
    // {
    //   id: 'mid-air-arc',
    //   type: 'arc',
    //   center: [20, 3.0, 5],
    //   radius: 10,
    //   count: 12,
    //   spread: Math.PI * 1.1,
    //   heightStep: 0.12,
    //   baseScale: 0.35,
    //   scaleWave: 0.15,
    // },
    // {
    //   id: 'long-far-arc',
    //   type: 'arc',
    //   center: [35, 4.5, -5],
    //   radius: 12,
    //   count: 18,
    //   spread: Math.PI * 1.3,
    //   heightStep: 0.16,
    //   baseScale: 0.35,
    //   scaleWave: 0.15,
    // },
    // {
    //   id: 'lonely-ring',
    //   type: 'single',
    //   center: [25, 6.0, 15],
    //   count: 1,
    //   baseScale: 0.45,
    //   scaleWave: 0,
    // },
    // {
    //   id: 'triple-burst',
    //   type: 'line',
    //   center: [5, 2.5, -10],
    //   length: 8,
    //   count: 3,
    //   heightStep: 0.2,
    //   baseScale: 0.35,
    //   scaleWave: 0.1,
    // },
  ];

  const colors = [
    '#2ecc71',
    '#3498db',
    '#e74c3c',
    '#f39c12',
    '#9b59b6',
    '#1abc9c',
    '#e67e22',
  ];

  const rings = React.useMemo(() => {
    const result = [];
    let globalIndex = 0;

    clustersConfig.forEach((cluster) => {
      const {
        id,
        type,
        center,
        radius = 6,
        count,
        spread = Math.PI,
        heightStep = 0,
        length = 6,
        points,
        baseScale = 0.35,
        scaleWave = 0.15,
      } = cluster;

      if (count <= 0) return;

      if (type === 'single') {
        const color = colors[globalIndex % colors.length];
        const s = baseScale;

        result.push({
          ringId: `${id}-${globalIndex}`,
          key: `${id}-${globalIndex}`,
          position: [center[0], center[1], center[2]],
          scale: [s, s, s],
          color,
        });
        globalIndex += 1;
        return;
      }

      if (type === 'line') {
        for (let i = 0; i < count; i++) {
          const t = count === 1 ? 0.5 : i / (count - 1); // 0 → 1
          const x = center[0] - length / 2 + t * length;
          const z = center[2];
          const y = center[1] + (i - (count - 1) / 2) * heightStep;

          const s = baseScale + scaleWave * Math.sin(t * Math.PI);
          const color = colors[globalIndex % colors.length];

          result.push({
            ringId: `${id}-${globalIndex}`,
            key: `${id}-${globalIndex}`,
            position: [x, y, z],
            scale: [s, s, s],
            color,
          });

          globalIndex += 1;
        }
        return;
      }

      if (type === 'path') {
        const positions = samplePath(points, count);
        positions.forEach((pos, i) => {
          const t = positions.length === 1 ? 0.5 : i / (positions.length - 1);
          const s = baseScale + scaleWave * Math.sin(t * Math.PI);
          const color = colors[globalIndex % colors.length];

          result.push({
            ringId: `${id}-${globalIndex}`,
            key: `${id}-${globalIndex}`,
            position: pos,
            scale: [s, s, s],
            color,
          });

          globalIndex += 1;
        });
        return;
      }

      // default: arc
      for (let i = 0; i < count; i++) {
        const t = count === 1 ? 0.5 : i / (count - 1);
        const angle = -spread / 2 + t * spread;

        const x = center[0] + Math.cos(angle) * radius;
        const z = center[2] + Math.sin(angle) * radius;
        const y = center[1] + (i - (count - 1) / 2) * heightStep;

        const s = baseScale + scaleWave * Math.sin(t * Math.PI);
        const color = colors[globalIndex % colors.length];

        result.push({
          ringId: `${id}-${globalIndex}`,
          key: `${id}-${globalIndex}`,
          position: [x, y, z],
          scale: [s, s, s],
          color,
        });

        globalIndex += 1;
      }
    });

    return result;
  }, [clustersConfig]);

  return (
    <>
      {rings.map((r) => (
        <Ring
          key={r.key}
          ringId={r.ringId}
          position={r.position}
          scale={r.scale}
          color={r.color}
          onCollect={onRingCollect}
        />
      ))}
    </>
  );
}
